import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pause, Sparkles, Gift, Download } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Waveform } from "@/components/Waveform";
import { BrandMark } from "@/components/BrandMark";
import { AudioPlayer } from "@/components/AudioPlayer";
import { api, apiError } from "@/lib/api";
import { SignedImg, signedUrl, openDownload, retrySigned } from "@/lib/media";
import { AmbientBed } from "@/lib/ambientBed";
import { toast } from "sonner";

export default function Story() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const [fam, setFam] = useState(null);
  const [story, setStory] = useState(null);
  const [idx, setIdx] = useState(-1);
  const [gen, setGen] = useState(false);
  const audioRef = useRef(null);
  const bedRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/story/${kind}`);
    setFam(data.family); setStory(data.story);
  };
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [kind]);
  useEffect(() => () => bedRef.current?.stop(), []);

  const generate = async () => {
    setGen(true);
    toast.info("Bringing the voices together…");
    try { const { data } = await api.post("/story/generate", { kind }); setStory(data); toast.success("Ready."); }
    catch (e) { toast.error(apiError(e)); } finally { setGen(false); }
  };

  const seq = story?.sequence || [];
  const startBed = () => { if (!bedRef.current) bedRef.current = new AmbientBed(); bedRef.current.start(); };
  const stopBed = () => bedRef.current?.stop();

  const playFrom = (s) => {
    setIdx(s);
    bedRef.current?.duck(seq[s]?.type !== "narration");
    signedUrl(seq[s].audio_path).then((u) => { if (audioRef.current && u) { audioRef.current.src = u; audioRef.current.play().catch(() => retrySigned(audioRef.current, seq[s].audio_path, () => { setIdx(-1); stopBed(); })); } })
      .catch(() => toast.error("That recording couldn't be played. Please try again."));
  };
  const onEnded = () => { if (idx + 1 < seq.length) playFrom(idx + 1); else { setIdx(-1); stopBed(); } };
  const toggle = () => {
    if (idx === -1) { startBed(); playFrom(0); }
    else { audioRef.current?.pause(); setIdx(-1); stopBed(); }
  };

  const download = () => {
    openDownload(`story-${kind}`).catch((e) => toast.error(apiError(e)));
  };

  if (!fam) return <div className="min-h-screen legacy-bg"><Navbar /></div>;
  const isGift = kind === "family";
  const title = story?.title || (isGift ? "Your Memories of Me" : "My Memories of You");
  const current = idx >= 0 ? seq[idx] : null;

  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/assemble")} className="flex items-center gap-2 text-[#B9B3C8] mb-6" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" /> Back to the Story Assembler
        </button>

        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">{isGift ? <Gift className="h-9 w-9 text-[#B57BFF]" /> : <BrandMark size={44} />}</div>
          <p className="eyebrow mb-2">{fam.family_name}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#EDE6F7]">{title}</h1>
          {fam.created_by && !isGift && <p className="text-[#8C84A0] mt-2">Told in the voice of {fam.created_by}</p>}
          {isGift && <p className="text-[#8C84A0] mt-2">A surprise, in the voices of the family</p>}
        </div>

        <audio ref={audioRef} onEnded={onEnded} className="hidden"
          onError={() => idx >= 0 && retrySigned(audioRef.current, seq[idx]?.audio_path, () => { toast.error("That recording couldn't be played. Please try again."); setIdx(-1); stopBed(); })} />

        {!story ? (
          <div className="glass-card p-10 text-center">
            <Sparkles className="h-12 w-12 text-[#B57BFF] mx-auto mb-5" />
            <h2 className="font-serif text-3xl text-[#EDE6F7] mb-3">This story hasn't been created yet</h2>
            <p className="text-[#B9B3C8] mb-7">Bring the voices together to hear it as one continuous story.</p>
            <button data-testid="generate-story-btn" onClick={generate} disabled={gen}
              className="gold-btn h-16 px-9 rounded-full text-xl">{gen ? "Creating…" : "Create this story"}</button>
          </div>
        ) : (
          <>
            <div className="glass-card p-8 mb-8 flex flex-col items-center text-center">
              <div className={`mb-6 ${idx !== -1 ? "" : "opacity-70"}`}><Waveform bars={40} height={56} active={idx !== -1} /></div>
              {current && (
                <div className="mb-6 w-full">
                  <p className="eyebrow mb-1">{current.type === "narration" ? "Narrator" : "Now playing"}</p>
                  <p className="font-serif text-2xl text-[#EDE6F7]">{current.chapter_title || current.label}</p>
                  {current.relationship && <p className="gold-text">{current.relationship}</p>}
                  {current.transcript && (
                    <p className="mt-3 text-lg text-[#B9B3C8] font-cormorant italic max-w-xl mx-auto">"{current.transcript}"</p>
                  )}
                </div>
              )}
              <button data-testid="play-story-btn" onClick={toggle}
                className="gold-btn h-16 px-10 rounded-full text-xl flex items-center">
                {idx === -1 ? <><Play className="h-6 w-6 mr-2" /> Play {isGift ? "the Surprise" : "Our Story"}</>
                            : <><Pause className="h-6 w-6 mr-2" /> Pause</>}
              </button>
              <button data-testid="download-story-btn" onClick={download}
                className="ghost-gold h-12 px-6 rounded-full mt-4 flex items-center">
                <Download className="h-5 w-5 mr-2" /> Download this story as one audio file
              </button>
            </div>

            <h2 className="font-serif text-3xl text-[#EDE6F7] mb-5">Chapters</h2>
            <div className="space-y-4">
              {seq.map((s, i) => (
                <div key={i} className={`glass-card p-5 ${idx === i ? "border-[rgba(139,44,255,0.5)]" : ""}`} data-testid={`story-item-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {s.photo ? <SignedImg path={s.photo} alt="" className="h-10 w-10 rounded-full object-cover border border-[#33264A]" />
                               : <span className="h-10 w-10 rounded-full bg-[#1A1222] border border-[#33264A] flex items-center justify-center font-serif gold-text">{(s.name || "•")[0]}</span>}
                      <p className="font-serif text-xl text-[#EDE6F7]">{s.chapter_title || s.label}</p>
                    </div>
                    <span className="text-xs uppercase tracking-widest text-[#5E5670]">{s.type === "narration" ? "narration" : s.type}</span>
                  </div>
                  <AudioPlayer path={s.audio_path} testId={`story-audio-${i}`} />
                  {s.transcript && s.type !== "narration" && (
                    <p className="mt-3 text-[#B9B3C8] font-cormorant text-lg">{s.transcript}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <button onClick={generate} disabled={gen} data-testid="regenerate-btn" className="text-[#8C84A0] underline">
                {gen ? "Rebuilding…" : "Rebuild with the latest replies"}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
